import type { KeyboardEventHandler } from 'react';
import { useMemo } from 'react';
import type { GroupBase, StylesConfig } from 'react-select';
import SelectAsync from 'react-select';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { AccountSearchResult } from '@trinity/chat';
import {
  buildSelectStyles,
  buildSelectTheme,
} from '@trinity/chat-components/components/Chat.Filters/Presentation.js';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useDebounceValue } from '@trinity/chat-components/hooks/useDebounceValue';

export type SelectAccountProps = {
  disabled?: boolean;
  inputValue: string;
  selectedAccount: AccountSearchResult | null;
  onInputChange: (value: string) => void;
  onSelection: (account: AccountSearchResult) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  territoryId?: string;
  queryFn: (params: {
    name: string;
    brandIdentifier: string | null;
    territoryId?: string;
  }) => Promise<AccountSearchResult[]>;
};

export function SelectAccount({
  disabled,
  inputValue,
  selectedAccount,
  onInputChange,
  onSelection,
  onKeyDown,
  territoryId,
  queryFn,
}: SelectAccountProps) {
  const theme = useTheme();
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const debouncedInputValue = useDebounceValue(inputValue, 250);
  const trimmedDebouncedInputValue = debouncedInputValue.trim();

  const searchQuery = useQuery<AccountSearchResult[]>({
    queryKey: [
      'chat',
      'account-search-autocomplete',
      trimmedDebouncedInputValue,
      brandIdentifier,
      territoryId,
    ],
    queryFn: () => queryFn({ name: trimmedDebouncedInputValue, brandIdentifier, territoryId }),
    enabled: trimmedDebouncedInputValue.length > 0 && !disabled,
    placeholderData: [],
  });

  const options = searchQuery.data ?? [];

  const selectStyles = useMemo(
    () =>
      buildSelectStyles(theme) as StylesConfig<AccountSearchResult, false, GroupBase<AccountSearchResult>>,
    [theme]
  );
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  return (
    <SelectWrap>
      <SelectAsync<AccountSearchResult>
        menuPortalTarget={document.body}
        isDisabled={disabled}
        onKeyDown={options.length > 0 ? undefined : onKeyDown}
        onChange={(option) => {
          if (option) {
            onSelection(option);
          }
        }}
        inputValue={inputValue}
        value={selectedAccount}
        isLoading={searchQuery.isFetching}
        options={options}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        noOptionsMessage={() => null}
        onInputChange={(value, meta) => {
          if (meta.action === 'input-change') {
            onInputChange(value);
          }
        }}
        placeholder=""
        styles={selectStyles}
        filterOption={() => true}
        theme={selectTheme}
      />
    </SelectWrap>
  );
}

const SelectWrap = styled.div({
  flex: 1,
  maxWidth: 280,
});
